// services/previewEngine.ts
import html2canvas from 'html2canvas';

export interface PreviewData {
  title: string;
  subtitle?: string;
  content: any;
  type?: 'auto' | 'table' | 'document' | 'chart' | 'survey';
  format?: 'auto' | 'portrait' | 'landscape';
  companyName?: string;
  year?: number;
  pageTitle?: string;
  moduleName?: string;
  columns?: Array<{
    key: string;
    title: string;
    width?: string;
    render?: (value: any) => string;
  }>;
  chartData?: any[];
  chartConfig?: {
    xAxisLabel: string;
    yAxisLabel: string;
    thresholds: {
      etki_hayati: number;
      etki_onemsiz: number;
      finans_hayati: number;
      finans_onemsiz: number;
    };
  };
}

export interface PreviewOptions {
  zoom: number;
  format: 'portrait' | 'landscape';
  quality: 'standard' | 'high';
}

class PreviewEngine {
  private detectContentType(content: any): 'table' | 'document' | 'chart' | 'survey' {
    if (content && content.anketBasligi && content.bolumler) {
      return 'survey';
    }
    if (Array.isArray(content) && content.length > 0) {
      return 'table';
    }
    if (typeof content === 'string' || (content && typeof content === 'object' && content.type)) {
      return 'document';
    }
    return 'chart';
  }

  private detectOptimalFormat(content: any, type: string): 'portrait' | 'landscape' {
    if (type === 'table' && Array.isArray(content) && content.length > 0) {
      const firstRow = content[0];
      const columnCount = Object.keys(firstRow).length;
      return columnCount > 3 ? 'landscape' : 'portrait';
    }
    return 'portrait';
  }

  async generatePDF(data: PreviewData, options: PreviewOptions): Promise<void> {
    // Lazy load pdfmake
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    const pdfMake = pdfMakeModule.default;
    pdfMake.vfs = pdfFontsModule.default;

    const isLandscape = options.format === 'landscape';
    const content: any[] = [];

    // Header
    content.push(
      { text: data.companyName || 'Firma', style: 'header', alignment: 'center' },
      { text: `${data.year || new Date().getFullYear()} Sürdürülebilirlik Raporu`, style: 'subheader', alignment: 'center' },
      { text: data.pageTitle || 'Bölüm', style: 'small', alignment: 'center', margin: [0, 0, 0, 10] }
    );

    // Anket verisi
    if (data.type === 'survey' && data.content?.anketBasligi) {
      const anket = data.content;
      
      content.push(
        { text: anket.anketBasligi, style: 'anketTitle', alignment: 'center', margin: [0, 15, 0, 15] }
      );
      
      if (anket.girisMetni) {
        content.push(
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: '#8D6E63' }], margin: [0, 0, 0, 10] },
          { text: anket.girisMetni, style: 'girisMetni', margin: [0, 0, 0, 10], alignment: 'justify' },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: '#8D6E63' }], margin: [0, 0, 0, 15] }
        );
      }
      
      anket.bolumler?.forEach((bolum: any, bolumIndex: number) => {
        content.push(
          { text: `${bolumIndex + 1}. ${bolum.bolumBasligi}`, style: 'bolumBaslik', margin: [0, 15, 0, 10], fillColor: '#f5f5f5', decoration: 'underline', decorationColor: '#8D6E63' }
        );
        
        bolum.sorular?.forEach((soru: any, soruIndex: number) => {
          content.push({ text: `${bolumIndex + 1}.${soruIndex + 1}. ${soru.metin}`, style: 'soruMetin', margin: [15, 8, 0, 5] });
          
          if (soru.tip === 'likert1_5') {
            content.push(
              { text: '○ 1        ○ 2        ○ 3        ○ 4        ○ 5', style: 'likertOptions', margin: [30, 3, 0, 3] },
              { columns: [
                { text: 'Kesinlikle Katılmıyorum', style: 'likertLabel', width: '50%' },
                { text: 'Kesinlikle Katılıyorum', style: 'likertLabel', width: '50%', alignment: 'right' }
              ], margin: [30, 0, 30, 8] }
            );
          }
          
          if (soru.tip === 'acik_uclu') {
            content.push(
              { canvas: [{ type: 'rect', x: 30, y: 0, w: 485, h: 50, lineWidth: 1, lineColor: '#8D6E63', dash: { length: 3 } }], margin: [0, 3, 0, 12] }
            );
          }
        });
      });
      
      if (anket.tesekkurMetni) {
        content.push(
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#cccccc' }], margin: [0, 20, 0, 10] },
          { text: anket.tesekkurMetni, style: 'tesekkurMetni', alignment: 'center', margin: [0, 0, 0, 0] }
        );
      }
    }
    // Tablo verisi
    else if (data.type === 'table' && Array.isArray(data.content) && data.content.length > 0) {
      // TSRS/GRI formatı
      if (data.content[0]?.displayNo && data.content[0]?.soruReferans) {
        content.push({
          table: {
            headerRows: 1,
            widths: ['5%', '15%', '40%', '40%'],
            body: [
              [{ text: 'No', style: 'tableHeader' }, { text: 'Referans', style: 'tableHeader' }, { text: 'Soru', style: 'tableHeader' }, { text: 'Cevap', style: 'tableHeader' }],
              ...data.content.map((item: any) => [
                { text: item.displayNo || '', style: 'tableCell' },
                { text: item.soruReferans || '', style: 'tableCell' },
                { text: item.soruMadde || '', style: 'tableCell' },
                { text: item.cevap || '-', style: 'tableCell' }
              ])
            ]
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#8D6E63' : (rowIndex % 2 === 0 ? '#f9f9f9' : null),
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#333333',
            vLineColor: () => '#333333'
          }
        });
      }
      // Kategori bazlı veri
      else if (data.content[0]?.category) {
        data.content.forEach((cat: any) => {
          content.push(
            { text: cat.category, style: 'categoryHeader', margin: [0, 5, 0, 3] },
            {
              table: {
                headerRows: 1,
                widths: ['5%', '15%', '40%', '40%'],
                body: [
                  [{ text: 'No', style: 'tableHeader' }, { text: 'Referans', style: 'tableHeader' }, { text: 'Soru', style: 'tableHeader' }, { text: 'Cevap', style: 'tableHeader' }],
                  ...(cat.items?.map((item: any) => [
                    { text: item.displayNo || '', style: 'tableCell' },
                    { text: item.soruReferans || '', style: 'tableCell' },
                    { text: item.soruMadde || '', style: 'tableCell' },
                    { text: item.cevap || '-', style: 'tableCell' }
                  ]) || [])
                ]
              },
              layout: {
                fillColor: (rowIndex: number) => rowIndex === 0 ? '#8D6E63' : (rowIndex % 2 === 0 ? '#f9f9f9' : null),
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#333333',
                vLineColor: () => '#333333'
              },
              margin: [0, 0, 0, 5]
            }
          );
        });
      }
      // Genel tablo
      else {
        const columns = data.columns || Object.keys(data.content[0]).map(key => ({ key, title: key }));
        content.push({
          table: {
            headerRows: 1,
            widths: columns.map(() => '*'),
            body: [
              columns.map(col => ({ text: col.title, style: 'tableHeader' })),
              ...data.content.map((row: any) => columns.map(col => ({ text: String(row[col.key] || '-'), style: 'tableCell' })))
            ]
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#8D6E63' : (rowIndex % 2 === 0 ? '#f9f9f9' : null),
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#333333',
            vLineColor: () => '#333333'
          }
        });
      }
    }
    // Grafik varsa ekle (tablo ile birlikte)
    if (data.chartData && data.chartConfig) {
      const chartElement = document.querySelector('.recharts-wrapper');
      if (chartElement) {
        try {
          const chartCanvas = await html2canvas(chartElement as HTMLElement, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
          });
          const chartImgData = chartCanvas.toDataURL('image/png');
          content.push(
            { text: '', pageBreak: 'before' },
            { text: 'Çifte Önemlilik Matrisi Grafiği', style: 'header', alignment: 'center', margin: [0, 0, 0, 15] },
            { image: chartImgData, width: isLandscape ? 700 : 500, alignment: 'center' }
          );
        } catch (error) {
          console.error('Grafik yakalanırken hata:', error);
        }
      }
    }

    // Footer
    content.push({ text: `${data.moduleName || 'Modül'} • ${new Date().toLocaleDateString('tr-TR')}`, style: 'footer', alignment: 'center', margin: [0, 20, 0, 0] });

    const docDefinition: any = {
      pageSize: 'A4',
      pageOrientation: isLandscape ? 'landscape' : 'portrait',
      pageMargins: [40, 60, 40, 60],
      content,
      styles: {
        header: { fontSize: 18, bold: true, color: '#334155' },
        subheader: { fontSize: 12, color: '#64748b' },
        small: { fontSize: 9, color: '#64748b' },
        anketTitle: { fontSize: 20, bold: true, color: '#8D6E63', decoration: 'underline' },
        girisMetni: { fontSize: 11, color: '#374151', lineHeight: 1.4 },
        bolumBaslik: { fontSize: 14, bold: true, color: '#5D4037', background: '#f5f5f5' },
        soruMetin: { fontSize: 11, color: '#1f2937', lineHeight: 1.3 },
        likertOptions: { fontSize: 10, color: '#424242', bold: true },
        likertLabel: { fontSize: 9, color: '#757575', italics: true },
        tesekkurMetni: { fontSize: 11, italics: true, color: '#8D6E63', bold: true },
        tableHeader: { fontSize: 8, bold: true, color: '#ffffff', fillColor: '#8D6E63' },
        tableCell: { fontSize: 7, color: '#000000' },
        categoryHeader: { fontSize: 9, bold: true, color: '#8D6E63' },
        footer: { fontSize: 8, color: '#9ca3af' }
      },
      defaultStyle: {
        font: 'Roboto'
      }
    };

    const fileName = `${data.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
  }

  processData(data: PreviewData): { processedData: PreviewData; options: PreviewOptions } {
    const type = data.type === 'auto' ? this.detectContentType(data.content) : data.type || 'document';
    const format = data.format === 'auto' ? this.detectOptimalFormat(data.content, type) : data.format || 'portrait';

    return {
      processedData: { ...data, type },
      options: {
        zoom: 100,
        format,
        quality: 'standard'
      }
    };
  }
}

export const previewEngine = new PreviewEngine();